import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import useCashRegisterStatus from './useCashRegisterStatus';
import toast from 'react-hot-toast';

/**
 * Custom hook for handling automatic cash register opening flow.
 * Provides a unified way to check if cash register is open and prompt user to open it
 * before completing transactions (sales or expenses).
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.hasExistingTransactions - Whether there are existing transactions today (to skip prompt)
 * @param {boolean} options.hasPermission - Whether user has permission to manage cash register
 * @returns {Object} Hook state and methods
 */
const useCashRegisterAutoOpen = ({ hasExistingTransactions = false, hasPermission = true } = {}) => {
    const { t } = useTranslation();
    const {
        isOpen: isCashRegisterOpen,
        loading: cashLoading,
        refetch: refetchCashStatus,
        cashRegister,
        openContext,
        canOpenAStation
    } = useCashRegisterStatus();

    const [showOpenCashModal, setShowOpenCashModal] = useState(false);
    const [pendingCallback, setPendingCallback] = useState(null);

    /**
     * Check if cash register needs to be opened and show prompt if needed.
     *
     * New routing logic:
     *  - User has own open register → proceed (no modal).
     *  - User has a closed responsible station → show "Abrir Caja" modal.
     *  - User has no responsible stations but business has ≥1 open register → proceed silently
     *    (payment routes automatically for 1 register; goes to PENDING_CASHIER for N).
     *  - User has no responsible stations AND zero registers → proceed (current behaviour,
     *    cashier_register_id will be null).
     *
     * @param {Function} saveCallback - Callback to execute after decision
     * @param {boolean} isEditing - Skip check for edit operations
     * @returns {Promise<boolean>} true = proceed with save, false = opening cash register
     */
    const checkAndPromptOpen = useCallback(async (saveCallback, isEditing = false) => {
        // Always proceed for edits
        if (isEditing) return true;

        // User already has their own register open → no action needed
        if (isCashRegisterOpen) return true;

        // User has no permission → proceed (backend handles rejection if needed)
        if (!hasPermission) return true;

        // User has existing transactions today → proceed
        if (hasExistingTransactions) return true;

        // User has a responsible station that is closed → show modal to open it
        if (canOpenAStation) {
            const result = await Swal.fire({
                title: t('cashRegisterPage.autoOpen.title', 'Caja Cerrada'),
                text: t('cashRegisterPage.autoOpen.message', 'La caja registradora no está abierta. ¿Desea abrirla antes de continuar?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('cashRegisterPage.autoOpen.openButton', 'Abrir Caja'),
                cancelButtonText: t('cashRegisterPage.autoOpen.continueWithout', 'Continuar sin abrir'),
                confirmButtonColor: 'var(--ev-success-solid)',
                cancelButtonColor: 'var(--ev-bg-subtle)'
            });

            if (result.isConfirmed) {
                setPendingCallback(() => saveCallback);
                setShowOpenCashModal(true);
                return false; // Wait for cash register to open
            }

            // User chose to continue without opening
            return true;
        }

        // No responsible station available — proceed regardless of open registers count.
        // If 1 open → payment auto-assigned by backend.
        // If N open → sale becomes PENDING_CASHIER.
        // If 0 open → cashier_register_id = null (current behaviour).
        return true;
    }, [isCashRegisterOpen, hasPermission, hasExistingTransactions, canOpenAStation, t]);


    /**
     * Handler for when cash register is successfully opened.
     * Executes pending callback and cleans up state.
     */
    const handleCashRegisterSuccess = useCallback(async () => {
        refetchCashStatus();
        toast.success(t('cashRegisterPage.toasts.opened'));

        if (pendingCallback) {
            await pendingCallback();
            setPendingCallback(null);
        }

        setShowOpenCashModal(false);
    }, [pendingCallback, refetchCashStatus, t]);

    /**
     * Handler for when user cancels opening cash register.
     * Still executes pending callback (save without opening) and cleans up state.
     */
    const handleCashRegisterCancel = useCallback(() => {
        if (pendingCallback) {
            pendingCallback();
            setPendingCallback(null);
        }

        setShowOpenCashModal(false);
    }, [pendingCallback]);

    /**
     * Handler for modal hide (called on any close).
     * Does not execute callback - only onSuccess or onCancel do.
     */
    const handleCashRegisterHide = useCallback(() => {
        setShowOpenCashModal(false);
    }, []);

    /**
     * Set pending callback externally (for modal-based flows like CreateExpenseModal)
     */
    const setPendingTransactionCallback = useCallback((callback) => {
        setPendingCallback(() => callback);
        setShowOpenCashModal(true);
    }, []);

    /**
     * Manually open the cash register modal (for UI buttons)
     */
    const openModal = useCallback(() => {
        setShowOpenCashModal(true);
    }, []);

    return {
        // State
        isCashRegisterOpen,
        cashLoading,
        cashRegister,
        openContext,
        canOpenAStation,
        showOpenCashModal,
        hasPendingTransaction: !!pendingCallback,

        // Methods
        checkAndPromptOpen,
        setPendingTransactionCallback,
        refetchCashStatus,
        openModal, // For manual trigger from UI buttons

        // Modal handlers (to be passed to OpenCashRegisterModal)
        modalProps: {
            show: showOpenCashModal,
            onHide: handleCashRegisterHide,
            onSuccess: handleCashRegisterSuccess,
            onCancel: handleCashRegisterCancel
        }
    };
};

export default useCashRegisterAutoOpen;
